import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Button from 'antd/lib/button';
import DatePicker from 'antd/lib/date-picker';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import { CSVLink } from 'react-csv';

import { getCSVEventsData } from '../../../../API';
import { config } from '../../../../config';

import './CSVDownloadModal.less';

const { REACT_APP_BUILD_ENV } = config;

const ALERT_STATE_OPTIONS = [
  { key: 'all', value: 'All Alerts' },
  { key: 'current', value: 'Current Alerts' },
  { key: 'past', value: 'Past Alerts' },
];

export const CSVDownloadModal = (props) => {
  const { Option } = Select;
  const { RangePicker } = DatePicker;

  const { filterOptions, onCancel, theme } = props;
  const { building } = useParams();
  const [alertState, setAlertState] = useState('all');
  const [tier, setTier] = useState();
  const [dateRange, setDateRange] = useState(null);
  const [system, setSystem] = useState();
  const [isDownloading, setIsDownloading] = useState(false);
  const [CSVData, setCSVData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState();
  const [errorMessage, setErrorMessage] = useState('');

  const csvLink = useRef(null);

  const buildFilters = () => {
    const filters = {};

    if (alertState === 'current') filters.is_active = true;
    if (alertState === 'past') filters.is_active = false;

    if (tier) filters.severity_level = tier;
    if (system) filters.system = system;

    if (dateRange && dateRange[0]) filters.start_timestamp = dateRange[0].clone().startOf('day').utc().format();
    if (dateRange && dateRange[1]) filters.end_timestamp = dateRange[1].clone().endOf('day').utc().format();

    return filters;
  };

  const downloadCSV = async () => {
    setIsDownloading(true);
    setErrorMessage('');

    try {
      const csvData = await getCSVEventsData(REACT_APP_BUILD_ENV, building, buildFilters());

      if (Array.isArray(csvData) && csvData.length > 0) {
        const columnOrder = ['Name', 'Severity', 'System', 'Event start', 'Event end'];

        const headers = Object.keys(csvData[0])
          .sort((a, b) => {
            const indexOf = (s) => columnOrder.findIndex((c) => c.toLowerCase() === s.toLowerCase());
            return indexOf(a) - indexOf(b);
          })
          .map((key) => ({
            label: columnOrder.find((c) => c.toLowerCase() === key.toLowerCase()) || key,
            key,
          }));
        setCsvHeaders(headers);
        setCSVData(csvData);

        setTimeout(() => {
          csvLink.current.link.click();
          onCancel();
        }, 100);
      } else {
        setErrorMessage('No alerts match the selected filters.');
      }
    } catch (error) {
      setErrorMessage(
        typeof error === 'string' && error
          ? error
          : 'We encountered an error while preparing your download. Please try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      className={`alerts-csv-modal alerts-csv-modal-${theme}`}
      centered
      open
      keyboard
      title="Alerts Export"
      footer={null}
      onCancel={onCancel}
    >
      <div className="alerts-csv-modal__field">
        <span className="alerts-csv-modal__label">Alerts</span>
        <Select
          className="alerts-csv-modal__select"
          value={alertState}
          onChange={(value) => {
            setAlertState(value);
            if (value === 'current') setDateRange(null);
          }}
        >
          {ALERT_STATE_OPTIONS.map((option) => {
            return <Option key={option.key} value={option.key}>{option.value}</Option>;
          })}
        </Select>
      </div>
      <div className="alerts-csv-modal__field">
        <span className="alerts-csv-modal__label">Tier</span>
        <Select
          className="alerts-csv-modal__select"
          placeholder="All Tiers"
          allowClear
          value={tier}
          onChange={(value) => setTier(value)}
        >
          {(filterOptions.severity_levels || []).map((option) => {
            return <Option key={option.key} value={option.key}>{option.value}</Option>;
          })}
        </Select>
      </div>
      <div className="alerts-csv-modal__field">
        <span className="alerts-csv-modal__label">Date Range</span>
        <RangePicker
          className="alerts-csv-modal__range-picker"
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          disabledDate={(current) => current && current > moment().endOf('day')}
          allowEmpty={[true, true]}
          inputReadOnly
          disabled={alertState === 'current'}
        />
      </div>
      <div className="alerts-csv-modal__field">
        <span className="alerts-csv-modal__label">System</span>
        <Select
          className="alerts-csv-modal__select"
          placeholder="All Systems"
          allowClear
          value={system}
          onChange={(value) => setSystem(value)}
        >
          {(filterOptions.hvac_systems || []).map((option) => {
            return <Option key={option.key} value={option.key}>{option.value}</Option>;
          })}
        </Select>
      </div>
      <Button
        size="large"
        className="alerts-csv-modal__button"
        loading={isDownloading}
        onClick={downloadCSV}
      >
        <span className="alerts-csv-modal__button-text">Download</span>
      </Button>
      {errorMessage && <div className="alerts-csv-modal__error">{errorMessage}</div>}
      <CSVLink
        data={CSVData}
        headers={csvHeaders}
        filename="alert_data.csv"
        className="hidden"
        ref={csvLink}
        target="_blank"
      />
    </Modal>
  );
};
